import { useEffect, useMemo, useState } from "react";
import { api, asArray } from "@/lib/api";
import PillSelect from "@/components/PillSelect";
import { useTick } from "@/context/LiveContext";
import VenueCard, { VenueCardSkeleton } from "@/components/VenueCard";
import CitySelect from "@/components/CitySelect";
import { Divider } from "@/components/Ornaments";
import { useCity } from "@/context/CityContext";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "@/lib/toast";
import { ArrowRight, ArrowUp, ArrowDown, Trash2, Plus, Save, Settings2, Landmark, ChevronDown } from "lucide-react";

export default function Highlighted() {
  const { byState, cities, states } = useCity();
  const tick = useTick();
  const { user } = useAuth();
  const [order, setOrder] = useState([]);
  const [byCity, setByCity] = useState({});
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [addCity, setAddCity] = useState("");

  useEffect(()=>{ api.get("/config/highlighted-cities").then(r=>{ const l = asArray(r.data); setOrder(l); if (!editing) setDraft(l); }).catch(()=>{}); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const [stateCities, setStateCities] = useState(null); // cities in the chosen state that actually have listings
  const [stateData, setStateData] = useState({});
  useEffect(() => {
    if (!state || city) { setStateCities(null); return; }
    let alive = true;
    setStateCities(null);
    api.get("/venues", { params: { state, limit: 200 } }).then((r) => {
      if (!alive) return;
      const g = {};
      for (const v of asArray(r.data)) (g[v.city] = g[v.city] || []).push(v);
      const names = Object.keys(g).sort((a, b) => g[b].length - g[a].length);
      setStateData(Object.fromEntries(names.map((n) => [n, g[n].sort((a, b) => (b.highlighted ? 1 : 0) - (a.highlighted ? 1 : 0)).slice(0, 3)])));
      setStateCities(names);
    }).catch(() => alive && setStateCities([]));
    return () => { alive = false; };
  }, [state, city, tick]);

  const shown = useMemo(() => {
    if (city) return [city];
    if (state) return stateCities || [];
    return order;
  }, [city, state, order, stateCities]);

  useEffect(()=>{
    let alive = true;
    if (state && !city) { if (stateCities) { setByCity(stateData); setLoading(false); } else setLoading(true); return () => { alive = false; }; }
    (async () => {
      if (!tick) setLoading(true);
      const res = {};
      await Promise.all(shown.slice(0, 12).map(async (c) => {
        try { const r = await api.get(`/venues/highlighted/${encodeURIComponent(c)}`); res[c] = asArray(r.data).slice(0, city ? 12 : 3); } catch { res[c] = []; }
      }));
      if (alive) { setByCity(res); setLoading(false); }
    })();
    return () => { alive = false; };
  }, [shown, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const stateOf = (name) => cities.find(c=>c.name===name)?.state;
  const move = (i, d) => { const n=[...draft]; const j=i+d; if (j<0||j>=n.length) return; [n[i],n[j]]=[n[j],n[i]]; setDraft(n); };
  const save = async () => { try { await api.put("/config/highlighted-cities", draft); setOrder(draft); setEditing(false); toast.success("City order saved"); } catch { toast.error("Failed to save"); } };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-12 md:py-16">
      <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-end mb-10">
        <div>
          <Divider label="Editor's picks"/>
          <h1 className="font-display text-4xl md:text-6xl text-[color:var(--maroon)] mt-3">Highlighted listings, city by city</h1>
          <p className="text-[color:var(--ink-2)] mt-2 text-[16px] max-w-2xl">Premium venues, caterers, decorators, photographers, anchors and musicians our team keeps an eye on. Filter by state, then pick a city.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <div className="w-full sm:w-60">
            <PillSelect variant="pill" value={state} onChange={(v)=>{ setState(v); setCity(""); }} testId="hl-state-select" minWidth={280}
              options={[{ value: "", label: "All states", Icon: Landmark }, ...states.map((st) => ({ value: st, label: st, hint: `${(byState.find((b) => b.state === st)?.cities.length || 0).toLocaleString("en-IN")} cities` }))]}/>
          </div>
          <div className="w-full sm:w-64"><CitySelect value={city} onChange={setCity} allowAll state={state || null} testId="hl-city-select"/></div>
          {user?.role === "super_admin" && !state && !city && (
            <button onClick={()=>{ setEditing(!editing); setDraft(order); }} className="btn-ghost !py-2.5 whitespace-nowrap" data-testid="hl-edit-order"><Settings2 className="w-4 h-4"/> {editing ? "Close" : "Arrange cities"}</button>
          )}
        </div>
      </div>

      {editing && (
        <div className="card-heritage p-6 mb-10" data-testid="hl-order-editor">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="font-display text-2xl text-[color:var(--maroon)]">Arrange city order</div>
            <div className="flex gap-2 items-center">
              <div className="w-56"><CitySelect value={addCity} onChange={setAddCity} testId="hl-add-city" placeholder="Add a city"/></div>
              <button onClick={()=>{ if (addCity && !draft.includes(addCity)) setDraft([...draft, addCity]); setAddCity(""); }} className="btn-ghost !py-2.5 !px-3" data-testid="hl-add-city-btn"><Plus className="w-4 h-4"/></button>
              <button onClick={save} className="btn-wine !py-2.5" data-testid="hl-save-order"><Save className="w-4 h-4"/> Save</button>
            </div>
          </div>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {draft.map((c, i)=>(
              <li key={c} className="flex items-center gap-3 bg-white rounded-xl border border-[color:var(--line)] px-3 py-2" data-testid={`hl-order-row-${c}`}>
                <span className="w-7 h-7 rounded-full bg-[color:var(--maroon)] text-[color:var(--cream-primary)] text-xs font-bold flex items-center justify-center">{i+1}</span>
                <span className="flex-1 text-sm font-medium">{c} <span className="text-xs text-neutral-400">{stateOf(c)}</span></span>
                <button onClick={()=>move(i,-1)} className="p-1.5 rounded-lg hover:bg-[color:var(--ivory)]" aria-label="Up"><ArrowUp className="w-4 h-4"/></button>
                <button onClick={()=>move(i,1)} className="p-1.5 rounded-lg hover:bg-[color:var(--ivory)]" aria-label="Down"><ArrowDown className="w-4 h-4"/></button>
                <button onClick={()=>setDraft(draft.filter(x=>x!==c))} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600" aria-label="Remove"><Trash2 className="w-4 h-4"/></button>
              </li>
            ))}
          </ol>
        </div>
      )}

      {loading && <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">{Array.from({length:6}).map((_,i)=><VenueCardSkeleton key={i}/>)}</div>}
      {!loading && shown.slice(0, 12).map((c, idx)=>(
        <section key={c} className="mb-14" data-testid={`hl-city-section-${c}`}>
          <div className="flex items-end justify-between mb-5 gap-4">
            <h2 className="font-display text-3xl md:text-4xl text-[color:var(--maroon)] flex items-center gap-3">
              {!state && !city && <span className="w-9 h-9 rounded-full border border-[color:var(--gold)] text-[color:var(--gold)] text-sm flex items-center justify-center font-body font-bold">{String(idx+1).padStart(2,"0")}</span>}
              {c} <span className="text-neutral-400 text-lg font-body font-normal">· {stateOf(c)}</span>
            </h2>
            <Link to={`/properties?city=${encodeURIComponent(c)}`} className="text-sm text-[color:var(--maroon)] font-semibold inline-flex items-center gap-1 hover:underline whitespace-nowrap">Explore all <ArrowRight className="w-4 h-4"/></Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {(byCity[c] || []).map(v=><VenueCard key={v.id} v={v}/>)}
            {!(byCity[c] || []).length && <div className="text-neutral-500 text-sm col-span-full border border-dashed border-[color:var(--gold)]/40 rounded-2xl py-10 text-center">No premium listings yet in {c}. <Link to="/pricing" className="text-[color:var(--maroon)] underline">Be the first →</Link></div>}
          </div>
        </section>
      ))}
      {!loading && !shown.length && <div className="text-center text-neutral-500 py-20">No listings in {state || "this state"} yet. <Link to="/register?role=venue_owner" className="text-[color:var(--maroon)] underline">List your business</Link></div>}
    </div>
  );
}
